import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  ClockIcon,
  ArrowPathIcon,
  DocumentArrowDownIcon,
} from "@heroicons/react/24/outline";
import type { LessonPlanOutput as LessonPlanOutputType } from "../../types/LessonPlan";
import { pdf } from "@react-pdf/renderer";
import LessonPlanPDF from "../LessonPlannerPdf";
import { Button, Text } from "@core/index";
import { CourseData } from "@packages/constant/CourseData";
import { trackEvent } from "@packages/hooks/useAnalytics";
import {
  LessonPlannerSectionDto,
  SectionContentDto,
} from "@packages/api/madlenAPI.schemas";

interface LessonPlanOutputProps {
  isLoading: boolean;
  data?: LessonPlanOutputType;
  planType: "MAARIF" | "DAILY";
  onBack: () => void;
}

const LessonPlanOutput: React.FC<LessonPlanOutputProps> = ({
  isLoading,
  data,
  planType,
  onBack,
}) => {
  const { t } = useTranslation();
  const { trackEvent: track } = trackEvent();
  const sendEvent = () => {
    track(
      {
        category: "LessonPlanner",
        action: "lessonPlanner.download.click",
        label: "LessonPlanner download click",
        nonInteraction: false,
      },
      {
        location: "/lesson-planning-tools/daily-plan/new",
        element: "pdf-download-button",
      },
    );
  };

  const handlePDFOpen = async () => {
    if (!data) return;
    sendEvent();
    const blob = await pdf(
      <LessonPlanPDF initialData={data} planType={planType} />,
    ).toBlob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };

  if (isLoading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <ArrowPathIcon className="w-8 h-8 text-emerald-500 animate-spin mx-auto mb-4" />
          <Text variant="h3">lessonPlanner.loading_lesson_planner</Text>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="border-b border-gray-200 dark:border-gray-700 py-4 px-6">
          <div className="flex items-center justify-between">
            <div>
              <Text variant={"h2"}>dailyLessonPlan.output.title</Text>
              <Text variant="p" status="secondary" size="sm" className="mt-1">
                {t("dailyLessonPlan.grade", {
                  grade: data.gradeLevel,
                })}{" "}
                -{" "}
                {t(
                  `courseType.${CourseData[
                    data.lesson as keyof typeof CourseData
                  ]?.name(data.gradeLevel ?? 8)}`,
                )}
              </Text>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                <ClockIcon className="w-4 h-4" />
                <span>
                  {Object.values(data.sections).reduce((total, section) => {
                    const sectionDuration = Number(section.duration) || 0;
                    return total + sectionDuration;
                  }, 0)}{" "}
                  {t("common.time.minutes")}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-8">
          {/* Main Sections */}
          <div className="space-y-6">
            {Object.entries(data.sections).map(
              ([key, section]: [string, LessonPlannerSectionDto]) => (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <Text
                      variant={"h3"}
                    >{`dailyLessonPlan.output.sections.${key.toLowerCase()}`}</Text>
                    {section.duration && Number(section.duration) > 0 && (
                      <Text variant="span" size="sm" status="secondary">
                        {section.duration} {t("common.time.minutes")}
                      </Text>
                    )}
                  </div>
                  <ul className="list-disc list-inside space-y-2">
                    {section.content.map(
                      (item: SectionContentDto, index: number) => (
                        <li
                          key={index}
                          className="text-gray-700 dark:text-gray-300 text-sm"
                        >
                          {item.details}
                        </li>
                      ),
                    )}
                  </ul>
                </motion.div>
              ),
            )}
          </div>

          {/* Additional Arrangements */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <Text variant={"h3"} className="mb-4">
              dailyLessonPlan.additional.arrangements.title
            </Text>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(data.additionalArrangements).map(
                ([key, arrangement]: [string, string[]]) => (
                  <motion.div
                    key={key}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4"
                  >
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                      {t(
                        `dailyLessonPlan.additional.arrangements.${key}.title`,
                      )}
                    </h4>
                    <ul className="list-disc list-inside space-y-1">
                      {arrangement.map((suggestion: string, index: number) => (
                        <li
                          key={index}
                          className="text-gray-700 dark:text-gray-300 text-sm"
                        >
                          {suggestion}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ),
              )}
            </div>
          </div>
          {/* Additional Instructions */}
          {data.additionalInstructions && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <Text variant={"h3"} className="mb-3">
                dailyLessonPlan.additional.instructions.title
              </Text>
              <Text variant="p" status="subtle" size="sm">
                {data.additionalInstructions}
              </Text>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 py-4 px-6">
          <div className="flex items-center justify-between space-x-3">
            <Button rounded="lg" size="sm" variant="outline" onClick={onBack}>
              {t("common.back")}
            </Button>
            <Button
              onClick={handlePDFOpen}
              variant="outline"
              size="sm"
              rounded="lg"
              bgColor="bg-emerald-600"
              textColor="text-white"
              borderColor="border-transparent"
              className="hover:bg-emerald-700"
            >
              <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
              {t("common.downloadPdf")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LessonPlanOutput;
